import { useMemo, useState } from "react";
import { addDays, endOfWeek, format, isSameDay, startOfWeek } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
} from "lucide-react";

import { SubpageHeader } from "@/components/global/SubpageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useEmployees } from "@/hooks/useEmployees";
import { useShifts } from "@/hooks/useShifts";
import type { Shift } from "@/types/shift.types";

type ViewMode = "week" | "day";

// ─── Palette de couleurs (1 couleur par employé) ──────────────────────────────
const SHIFT_COLORS = [
  { bg: "bg-sky-100",     text: "text-sky-800",     border: "border-sky-200"     },
  { bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-200"   },
  { bg: "bg-violet-100",  text: "text-violet-800",  border: "border-violet-200"  },
  { bg: "bg-rose-100",    text: "text-rose-800",    border: "border-rose-200"    },
  { bg: "bg-teal-100",    text: "text-teal-800",    border: "border-teal-200"    },
  { bg: "bg-orange-100",  text: "text-orange-800",  border: "border-orange-200"  },
  { bg: "bg-blue-100",    text: "text-blue-800",    border: "border-blue-200"    },
  { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
  { bg: "bg-pink-100",    text: "text-pink-800",    border: "border-pink-200"    },
  { bg: "bg-indigo-100",  text: "text-indigo-800",  border: "border-indigo-200"  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getWeekDays(date: Date): Date[] {
  const monday = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

function formatTime(iso: string) {
  return format(new Date(iso), "HH:mm");
}

function getShiftMinutes(shift: Shift): number {
  if (shift.status !== "PLANNED") return 0;
  const start = new Date(shift.startTime).getTime();
  const end = new Date(shift.endTime).getTime();
  const diffMin = Math.floor((end - start) / 60000);
  return Math.max(0, diffMin - (shift.breakMinutes ?? 0));
}

function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

/** Détecte un congé / absence à partir de la note du shift */
function getLeaveStyle(note: string | null): string | null {
  if (!note) return null;
  const n = note.toLowerCase();
  if (n.includes("congé") || n.includes("conge") || n.includes("vacance")) {
    return "bg-cyan-100 text-cyan-800 border-cyan-200";
  }
  if (
    n.includes("maladie") ||
    n.includes("arrêt") ||
    n.includes("arret") ||
    n.includes("accident")
  ) {
    return "bg-gray-100 text-gray-500 border-gray-200";
  }
  return null;
}

// ─── Cellule semaine (mini) ───────────────────────────────────────────────────

interface ShiftCellProps {
  shifts: Shift[];
  color: (typeof SHIFT_COLORS)[number];
}

const ShiftCell = ({ shifts, color }: ShiftCellProps) => {
  if (shifts.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 w-full">
      {shifts.map((shift) => {
        const isCancelled = shift.status === "CANCELLED";
        const leaveStyle = getLeaveStyle(shift.note);
        const mins = getShiftMinutes(shift);

        if (isCancelled) {
          return (
            <div
              key={shift.id}
              className="rounded-md px-2 py-1.5 text-[11px] border bg-gray-50 text-gray-400 border-dashed border-gray-300 line-through text-center"
            >
              {formatTime(shift.startTime)} – {formatTime(shift.endTime)}
            </div>
          );
        }

        if (leaveStyle) {
          return (
            <div key={shift.id} className={`rounded-md px-2 py-1.5 text-[11px] border ${leaveStyle}`}>
              <div className="font-semibold text-center leading-tight">{shift.note}</div>
              {mins > 0 && (
                <div className="text-center font-bold mt-0.5">{formatDuration(mins)}</div>
              )}
            </div>
          );
        }

        return (
          <div key={shift.id} className={`rounded-md px-2 py-1.5 text-[11px] border ${color.bg} ${color.text} ${color.border}`}>
            <div className="font-semibold text-center tabular-nums leading-tight">
              {formatTime(shift.startTime)} – {formatTime(shift.endTime)}
            </div>
            <div className="text-center font-bold mt-0.5">{formatDuration(mins)}</div>
            {shift.note && (
              <div className="text-center opacity-70 truncate leading-tight mt-0.5">{shift.note}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Ligne créneau vue jour ───────────────────────────────────────────────────

interface DayShiftRowProps {
  shift: Shift;
  color: (typeof SHIFT_COLORS)[number];
}

const DayShiftRow = ({ shift, color }: DayShiftRowProps) => {
  const isCancelled = shift.status === "CANCELLED";
  const leaveStyle = getLeaveStyle(shift.note);
  const mins = getShiftMinutes(shift);

  if (isCancelled) {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="w-1 self-stretch rounded-full bg-gray-300" />
        <div className="flex-1">
          <div className="text-sm text-gray-400 line-through tabular-nums">
            {formatTime(shift.startTime)} → {formatTime(shift.endTime)}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Annulé</div>
        </div>
      </div>
    );
  }

  if (leaveStyle) {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        <span className={`w-1 self-stretch rounded-full ${leaveStyle.includes("cyan") ? "bg-cyan-300" : "bg-gray-300"}`} />
        <div className="flex-1">
          <div className="text-sm font-semibold">{shift.note}</div>
          {mins > 0 && (
            <div className="text-xs text-muted-foreground">{formatDuration(mins)}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className={`w-1 self-stretch rounded-full ${color.border.replace("border-", "bg-")}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold tabular-nums ${color.text}`}>
          {formatTime(shift.startTime)} → {formatTime(shift.endTime)}
        </div>
        {shift.note && (
          <div className="text-xs text-muted-foreground truncate">{shift.note}</div>
        )}
      </div>
      <span className="text-sm font-bold text-muted-foreground shrink-0">
        {formatDuration(mins)}
      </span>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const Planning = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

  const {
    data: employeesData,
    isLoading: employeesLoading,
    isError: employeesError,
  } = useEmployees();

  const employees = useMemo(
    () =>
      [...(employeesData?.employees ?? [])].sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.trim().toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.trim().toLowerCase();
        return nameA.localeCompare(nameB, "fr");
      }),
    [employeesData]
  );

  const {
    data: shiftsData,
    isLoading: shiftsLoading,
    isError: shiftsError,
  } = useShifts({
    from: format(weekStart, "yyyy-MM-dd"),
    to: format(addDays(weekEnd, 1), "yyyy-MM-dd"),
  });
  const shifts = shiftsData?.shifts ?? [];

  const employeeIds = useMemo(
    () => new Set(employees.map((e) => e.id)),
    [employees]
  );

  // Shifts par case (employeeId__date)
  const shiftsByCell = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of shifts) {
      const displayDate = s.workDate ? new Date(s.workDate) : new Date(s.startTime);
      const key = `${s.employeeId}__${format(displayDate, "yyyy-MM-dd")}`;
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const aH = new Date(a.startTime).getHours();
        const bH = new Date(b.startTime).getHours();
        if (aH < 6 && bH >= 6) return 1;
        if (aH >= 6 && bH < 6) return -1;
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });
    }
    return map;
  }, [shifts]);

  const getCellShifts = (employeeId: string, day: Date) =>
    shiftsByCell.get(`${employeeId}__${format(day, "yyyy-MM-dd")}`) ?? [];

  // Total minutes par employé sur la semaine (PLANNED uniquement)
  const totalMinutesByEmployee = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of shifts) {
      if (!employeeIds.has(s.employeeId)) continue;
      map.set(s.employeeId, (map.get(s.employeeId) ?? 0) + getShiftMinutes(s));
    }
    return map;
  }, [shifts, employeeIds]);

  // Vue jour : employés travaillant ce jour
  const workingEmployeesForDay = useMemo(
    () => employees.filter((e) => getCellShifts(e.id, currentDate).length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, currentDate, shiftsByCell]
  );

  const stepDays = viewMode === "day" ? 1 : 7;
  const handlePrev = () => setCurrentDate((d) => addDays(d, -stepDays));
  const handleNext = () => setCurrentDate((d) => addDays(d, stepDays));
  const handleToday = () => setCurrentDate(new Date());

  const isLoading = employeesLoading || shiftsLoading;
  const hasError = employeesError || shiftsError;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gray-50">
      <SubpageHeader
        pageTitle="Planning"
        right={
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrev}
              aria-label={viewMode === "day" ? "Jour précédent" : "Semaine précédente"}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              <CalendarDays className="h-4 w-4 mr-1" />
              Auj.
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              aria-label={viewMode === "day" ? "Jour suivant" : "Semaine suivante"}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-3">

        {/* Barre toggle + libellé période */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            {viewMode === "day" ? (
              <>
                Planning du{" "}
                <span className="font-semibold text-foreground capitalize">
                  {format(currentDate, "EEEE d MMMM yyyy", { locale: fr })}
                </span>
              </>
            ) : (
              <>
                Semaine du{" "}
                <span className="font-semibold text-foreground">
                  {format(weekStart, "d MMMM", { locale: fr })}
                </span>{" "}
                au{" "}
                <span className="font-semibold text-foreground">
                  {format(weekEnd, "d MMMM yyyy", { locale: fr })}
                </span>
              </>
            )}
          </p>

          <div className="inline-flex rounded-lg border bg-white p-0.5 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode("week")}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-sm transition ${
                viewMode === "week"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Semaine
            </button>
            <button
              type="button"
              onClick={() => setViewMode("day")}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-sm transition ${
                viewMode === "day"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <List className="h-3.5 w-3.5" />
              Jour
            </button>
          </div>
        </div>

        {hasError && (
          <Card className="p-4 text-sm text-destructive">
            Impossible de charger le planning. Vérifiez la connexion.
          </Card>
        )}

        {isLoading && (
          <Card className="p-6 text-center text-muted-foreground text-sm">
            Chargement du planning…
          </Card>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VUE SEMAINE                                               */}
        {/* ══════════════════════════════════════════════════════════ */}
        {!isLoading && !hasError && viewMode === "week" && (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[780px]">

                {/* En-tête — chaque jour est cliquable pour ouvrir la vue jour */}
                <div className="grid grid-cols-[150px_repeat(7,minmax(90px,1fr))_72px] border-b bg-gray-50/80">
                  <div className="px-3 py-2.5 border-r" />
                  {weekDays.map((day) => {
                    const isToday = isSameDay(day, new Date());
                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        onClick={() => { setCurrentDate(day); setViewMode("day"); }}
                        className={`px-2 py-2 text-center border-r last:border-r-0 transition hover:bg-primary/5 ${
                          isToday ? "bg-primary/10" : ""
                        }`}
                      >
                        <div className={`text-[10px] font-semibold uppercase tracking-wider ${
                          isToday ? "text-primary" : "text-muted-foreground"
                        }`}>
                          {format(day, "EEE", { locale: fr }).replace(".", "")}
                        </div>
                        <div className={`text-base font-bold leading-tight ${
                          isToday ? "text-primary" : "text-foreground"
                        }`}>
                          {format(day, "d")}
                        </div>
                        <div className="text-[10px] text-muted-foreground capitalize">
                          {format(day, "MMM", { locale: fr })}
                        </div>
                      </button>
                    );
                  })}
                  <div className="px-2 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide text-center border-l">
                    Total
                  </div>
                </div>

                {employees.length === 0 && (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    Aucun employé.
                  </div>
                )}

                {employees.map((employee, idx) => {
                  const color = SHIFT_COLORS[idx % SHIFT_COLORS.length];
                  const totalWeek = totalMinutesByEmployee.get(employee.id) ?? 0;
                  const initials = (employee.firstName?.[0] ?? "") + (employee.lastName?.[0] ?? "");

                  return (
                    <div
                      key={employee.id}
                      className="grid grid-cols-[150px_repeat(7,minmax(90px,1fr))_72px] border-b last:border-b-0"
                    >
                      <div className="px-3 py-2 flex items-center gap-2 border-r">
                        <span className={`w-6 h-6 rounded-full ${color.bg} ${color.text} text-[9px] font-bold flex items-center justify-center shrink-0 ring-1 ${color.border}`}>
                          {initials}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[12px] font-semibold text-foreground truncate leading-tight">
                            {employee.firstName}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate leading-tight">
                            {employee.lastName}
                          </div>
                        </div>
                      </div>

                      {weekDays.map((day) => {
                        const cellShifts = getCellShifts(employee.id, day);
                        const isToday = isSameDay(day, new Date());
                        return (
                          <div
                            key={day.toISOString()}
                            className={`p-1.5 border-r last:border-r-0 min-h-[60px] flex items-start ${isToday ? "bg-primary/5" : ""}`}
                          >
                            <ShiftCell shifts={cellShifts} color={color} />
                          </div>
                        );
                      })}

                      <div className="px-2 py-2 flex items-center justify-center border-l bg-gray-50/60">
                        <span className="text-sm font-bold text-foreground">
                          {totalWeek > 0 ? formatDuration(totalWeek) : "—"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* VUE JOUR                                                  */}
        {/* ══════════════════════════════════════════════════════════ */}
        {!isLoading && !hasError && viewMode === "day" && (
          <div className="space-y-2">

            {/* Aucun travaillant ce jour */}
            {workingEmployeesForDay.length === 0 && (
              <Card className="p-8 text-center text-sm text-muted-foreground">
                Personne ne travaille ce jour.
              </Card>
            )}

            {/* Une carte par employé qui travaille */}
            {workingEmployeesForDay.map((employee) => {
              const idx = employees.indexOf(employee);
              const color = SHIFT_COLORS[idx % SHIFT_COLORS.length];
              const dayShifts = getCellShifts(employee.id, currentDate);
              const totalDay = dayShifts.reduce((acc, s) => acc + getShiftMinutes(s), 0);
              const initials = (employee.firstName?.[0] ?? "") + (employee.lastName?.[0] ?? "");

              return (
                <Card key={employee.id} className="overflow-hidden">
                  {/* En-tête employé */}
                  <div className={`flex items-center justify-between px-4 py-3 border-b ${color.bg}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full bg-white/60 ${color.text} text-[11px] font-bold flex items-center justify-center shrink-0 ring-1 ${color.border}`}>
                        {initials}
                      </span>
                      <span className={`font-semibold text-sm ${color.text}`}>
                        {employee.firstName} {employee.lastName}
                      </span>
                    </div>
                    {totalDay > 0 && (
                      <span className={`text-sm font-bold ${color.text} opacity-80`}>
                        {formatDuration(totalDay)}
                      </span>
                    )}
                  </div>

                  {/* Créneaux du jour */}
                  <div className="divide-y">
                    {dayShifts.map((shift) => (
                      <DayShiftRow key={shift.id} shift={shift} color={color} />
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
